import { useState, useRef } from "react";
import { Upload, FileText, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useBulkInsertProducts, type ProductInsert } from "@/hooks/useProducts";
import { toast } from "sonner";

type ImportStatus = "idle" | "parsing" | "importing" | "done" | "error";

const AdminImport = () => {
  const [status, setStatus] = useState<ImportStatus>("idle");
  const [fileName, setFileName] = useState("");
  const [parsedCount, setParsedCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  const bulkInsert = useBulkInsertProducts();

  const parseCSV = (text: string): Record<string, string>[] => {
    const lines = text.split("\n");
    if (lines.length < 2) return [];

    // Parse header
    const headers: string[] = [];
    let current = "";
    let inQuotes = false;
    for (const char of lines[0]) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        headers.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    headers.push(current.trim());

    // Parse rows
    const rows: Record<string, string>[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values: string[] = [];
      let val = "";
      let inQ = false;
      for (const char of line) {
        if (char === '"') {
          inQ = !inQ;
        } else if (char === "," && !inQ) {
          values.push(val.trim());
          val = "";
        } else {
          val += char;
        }
      }
      values.push(val.trim());

      const row: Record<string, string> = {};
      headers.forEach((h, idx) => {
        row[h] = values[idx] || "";
      });
      rows.push(row);
    }

    return rows;
  };

  const mapRow = (r: Record<string, string>): ProductInsert | null => {
    const name = r["Name"];
    if (!name) return null;

    const type = r["Type"] as "simple" | "variable" | "variation";
    if (!["simple", "variable", "variation"].includes(type)) return null;

    return {
      wc_id: r["ID"] ? Number(r["ID"]) : undefined,
      type,
      sku: r["SKU"] || undefined,
      name,
      published: r["Published"] === "1",
      is_featured: r["Is featured?"] === "1",
      visibility: r["Visibility in catalog"] || "visible",
      short_description: r["Short description"] || undefined,
      description: r["Description"] || undefined,
      tax_status: r["Tax status"] || "taxable",
      in_stock: r["In stock?"] !== "0",
      stock: r["Stock"] ? Number(r["Stock"]) : undefined,
      sale_price: r["Sale price"] ? Number(r["Sale price"]) : undefined,
      regular_price: r["Regular price"] ? Number(r["Regular price"]) : undefined,
      categories: r["Categories"] || undefined,
      tags: r["Tags"] || undefined,
      images: r["Images"] || undefined,
      parent_sku: r["Parent"] || undefined,
      attribute_1_name: r["Attribute 1 name"] || undefined,
      attribute_1_values: r["Attribute 1 value(s)"] || undefined,
      attribute_2_name: r["Attribute 2 name"] || undefined,
      attribute_2_values: r["Attribute 2 value(s)"] || undefined,
      brands: r["Brands"] || undefined,
    };
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setStatus("parsing");
    setErrorMsg("");

    try {
      const text = await file.text();
      const rows = parseCSV(text);
      const products = rows.map(mapRow).filter(Boolean) as ProductInsert[];
      setParsedCount(products.length);

      if (products.length === 0) {
        setStatus("error");
        setErrorMsg("No valid products found in CSV");
        return;
      }

      setStatus("importing");

      // Insert in batches of 50
      const batchSize = 50;
      for (let i = 0; i < products.length; i += batchSize) {
        const batch = products.slice(i, i + batchSize);
        await bulkInsert.mutateAsync(batch);
      }

      setStatus("done");
      toast.success(`Successfully imported ${products.length} products!`);
    } catch (err: any) {
      setStatus("error");
      setErrorMsg(err.message);
      toast.error("Import failed: " + err.message);
    }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-2xl font-bold mb-2">Bulk Import</h1>
      <p className="text-sm text-muted-foreground font-body mb-8">
        Upload a WooCommerce product export CSV to insert or update products.
      </p>

      <div className="bg-card rounded-lg border border-border p-8">
        {status === "idle" && (
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed border-border rounded-lg p-12 text-center cursor-pointer hover:border-primary/50 transition-colors"
          >
            <Upload className="w-10 h-10 mx-auto mb-4 text-muted-foreground" />
            <p className="font-body font-medium mb-1">Click to upload CSV</p>
            <p className="text-sm text-muted-foreground font-body">
              WooCommerce product export format
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              onChange={handleFile}
              className="hidden"
            />
          </div>
        )}

        {status === "parsing" && (
          <div className="text-center py-8">
            <FileText className="w-10 h-10 mx-auto mb-4 text-primary animate-pulse" />
            <p className="font-body font-medium">Parsing {fileName}…</p>
          </div>
        )}

        {status === "importing" && (
          <div className="text-center py-8">
            <Upload className="w-10 h-10 mx-auto mb-4 text-primary animate-bounce" />
            <p className="font-body font-medium">
              Importing {parsedCount} products…
            </p>
            <p className="text-sm text-muted-foreground font-body mt-1">
              This may take a moment
            </p>
          </div>
        )}

        {status === "done" && (
          <div className="text-center py-8">
            <CheckCircle className="w-10 h-10 mx-auto mb-4 text-green-500" />
            <p className="font-body font-medium text-green-700">
              Successfully imported {parsedCount} products!
            </p>
            <Button
              onClick={() => {
                setStatus("idle");
                setFileName("");
                setParsedCount(0);
              }}
              variant="outline"
              className="mt-4"
            >
              Import Another
            </Button>
          </div>
        )}

        {status === "error" && (
          <div className="text-center py-8">
            <AlertCircle className="w-10 h-10 mx-auto mb-4 text-destructive" />
            <p className="font-body font-medium text-destructive">{errorMsg}</p>
            <Button
              onClick={() => {
                setStatus("idle");
                setFileName("");
                setErrorMsg("");
              }}
              variant="outline"
              className="mt-4"
            >
              Try Again
            </Button>
          </div>
        )}
      </div>

      <div className="mt-8 bg-card rounded-lg border border-border p-6">
        <h2 className="font-display font-semibold mb-3">Expected CSV Format</h2>
        <p className="text-sm text-muted-foreground font-body mb-3">
          The CSV should follow the WooCommerce product export format. Key columns:
        </p>
        <ul className="text-sm text-muted-foreground font-body space-y-1 list-disc ml-4">
          <li><strong>ID</strong> – WooCommerce product ID</li>
          <li><strong>Type</strong> – simple, variable, or variation</li>
          <li><strong>SKU</strong> – Unique product identifier (used for upsert)</li>
          <li><strong>Name</strong> – Product name (required)</li>
          <li><strong>Regular price</strong> – Product price</li>
          <li><strong>Categories</strong> – Comma-separated categories</li>
          <li><strong>Images</strong> – Comma-separated image URLs</li>
          <li><strong>Attribute 1 name / value(s)</strong> – For variable products</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminImport;
